import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MainLayout from '../components/layout/MainLayout';
import { useTheme } from '../context/ThemeContext';
import {
  Plus, Search, Filter, Grid3X3, List, FileText, File,
  Folder, MoreVertical, Download, Share2, Trash2, Eye,
  Clock, User, Star, Sparkles, PenTool, Upload
} from 'lucide-react';

const templates = [
  { id: 1, name: 'Writ Petition', category: 'Constitutional', uses: 1250, icon: '📜' },
  { id: 2, name: 'Civil Suit', category: 'Civil', uses: 980, icon: '⚖️' },
  { id: 3, name: 'Bail Application', category: 'Criminal', uses: 856, icon: '🔓' },
  { id: 4, name: 'Appeal Memo', category: 'Appeals', uses: 742, icon: '📄' },
  { id: 5, name: 'Legal Notice', category: 'General', uses: 1520, icon: '📨' },
  { id: 6, name: 'Affidavit', category: 'General', uses: 2100, icon: '📝' },
];

const recentDocuments = [
  { id: 1, name: 'Reply to WP 1234-2024.docx', type: 'docx', size: '245 KB', modified: '2 hours ago', status: 'draft' },
  { id: 2, name: 'Bail Application - Sharma.pdf', type: 'pdf', size: '180 KB', modified: '5 hours ago', status: 'final' },
  { id: 3, name: 'Settlement Agreement.docx', type: 'docx', size: '320 KB', modified: 'Yesterday', status: 'review' },
  { id: 4, name: 'Court Order - CS 567.pdf', type: 'pdf', size: '890 KB', modified: '2 days ago', status: 'final' },
];

const Documents = () => {
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('templates');
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  return (
    <MainLayout title="Documents" subtitle="Draft, manage and organize legal documents">
      {/* Action Bar */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-6"
      >
        {/* Tabs */}
        <div className="flex items-center gap-1 p-1 rounded-xl glass-card">
          {['templates', 'my-documents', 'shared'].map((tab) => (
            <motion.button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${
                activeTab === tab ? 'bg-gradient-to-r from-[#1E3A5F] to-[#2D8CFF] text-white' : ''
              }`}
              whileTap={{ scale: 0.95 }}
            >
              {tab.replace('-', ' ')}
            </motion.button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <motion.button
            className="flex items-center gap-2 px-4 py-3 rounded-xl glass-card"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Upload className="w-5 h-5" />
            <span>Upload</span>
          </motion.button>
          <motion.button
            className="flex items-center gap-2 px-5 py-3 rounded-xl btn-primary"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Sparkles className="w-5 h-5" />
            <span>AI Draft</span>
          </motion.button>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {activeTab === 'templates' && (
          <motion.div
            key="templates"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* AI Draft Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-8 rounded-2xl relative overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(30,58,95,0.95) 0%, rgba(45,140,255,0.95) 100%)'
              }}
            >
              {/* Animated Background Elements */}
              <motion.div
                className="absolute top-0 right-0 w-64 h-64 rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.3) 0%, transparent 70%)' }}
                animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
                transition={{ duration: 10, repeat: Infinity }}
              />
              
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <Sparkles className="w-8 h-8 text-yellow-400" />
                    <span className="px-3 py-1 text-xs bg-yellow-400/20 text-yellow-400 rounded-full">NEW</span>
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">AI Document Drafting</h2>
                  <p className="text-white/70 max-w-lg">
                    Describe what you need in plain English, and our AI will generate a professional legal document in seconds.
                  </p>
                </div>
                <motion.button
                  className="px-6 py-4 rounded-xl bg-white text-[#1E3A5F] font-semibold flex items-center gap-2"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <PenTool className="w-5 h-5" />
                  Start Drafting
                </motion.button>
              </div>
            </motion.div>

            {/* Templates Grid */}
            <h3 className="text-lg font-semibold mb-4">Popular Templates</h3>
            <div className="grid grid-cols-3 gap-6">
              {templates.map((template, index) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -5 }}
                  onClick={() => setSelectedTemplate(template)}
                  className="glass-card p-6 cursor-pointer group relative overflow-hidden"
                >
                  {/* Hover Glow */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                  
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-4xl">{template.icon}</span>
                      <motion.button
                        className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-gray-500/10"
                        whileHover={{ scale: 1.1 }}
                      >
                        <Star className="w-5 h-5" />
                      </motion.button>
                    </div>
                    <h4 className="font-semibold text-lg mb-1">{template.name}</h4>
                    <p className="text-sm text-gray-500 mb-4">{template.category}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{template.uses.toLocaleString()} uses</span>
                      <motion.button
                        className="text-sm text-blue-500 font-medium"
                        whileHover={{ x: 5 }}
                      >
                        Use Template →
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {activeTab === 'my-documents' && (
          <motion.div
            key="documents"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Quick Stats */}
            <div className="grid grid-cols-4 gap-4 mb-6">
              {[
                { label: 'Total Documents', value: '156', icon: FileText, color: 'blue' },
                { label: 'Drafts', value: '12', icon: PenTool, color: 'yellow' },
                { label: 'Shared', value: '24', icon: Share2, color: 'purple' },
                { label: 'Storage Used', value: '2.4 GB', icon: Folder, color: 'green' },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-4 flex items-center gap-4"
                >
                  <div className={`p-3 rounded-xl bg-${stat.color}-500/20`}>
                    <stat.icon className={`w-5 h-5 text-${stat.color}-500`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-sm text-gray-500">{stat.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Documents List */}
            <div className="glass-card overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A8B] text-white">
                    <th className="px-6 py-4 text-left">Name</th>
                    <th className="px-6 py-4 text-left">Type</th>
                    <th className="px-6 py-4 text-left">Size</th>
                    <th className="px-6 py-4 text-left">Modified</th>
                    <th className="px-6 py-4 text-left">Status</th>
                    <th className="px-6 py-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentDocuments.map((doc, index) => (
                    <motion.tr
                      key={doc.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="border-b border-gray-500/10 hover:bg-blue-500/5 cursor-pointer"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            doc.type === 'pdf' ? 'bg-red-500/20 text-red-500' : 'bg-blue-500/20 text-blue-500'
                          }`}>
                            <FileText className="w-5 h-5" />
                          </div>
                          <span className="font-medium">{doc.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 uppercase text-sm">{doc.type}</td>
                      <td className="px-6 py-4 text-gray-500">{doc.size}</td>
                      <td className="px-6 py-4 text-gray-500">{doc.modified}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs rounded-full ${
                          doc.status === 'final' ? 'bg-green-500/20 text-green-500' :
                          doc.status === 'draft' ? 'bg-yellow-500/20 text-yellow-500' :
                          'bg-blue-500/20 text-blue-500'
                        }`}>
                          {doc.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {[Eye, Download, Share2, Trash2].map((Icon, i) => (
                            <motion.button
                              key={i}
                              className="p-2 rounded-lg hover:bg-gray-500/10"
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Icon className="w-4 h-4" />
                            </motion.button>
                          ))}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {activeTab === 'shared' && (
          <motion.div
            key="shared"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="glass-card p-12 text-center"
          >
            <Share2 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2">No Shared Documents</h3>
            <p className="text-gray-500 mb-6">Documents shared with you will appear here</p>
          </motion.div>
        )}
      </AnimatePresence>
    </MainLayout>
  );
};

export default Documents;
